import React, { cloneElement, createContext, isValidElement, useContext } from 'react';
import { Tooltip } from '../components/Tooltip';
import { DEFAULT_FILES_CAPABILITIES, type ResolvedFilesCapabilities } from './types';

/**
 * Ambient configuration for the files module. FilesPanel provides the resolved
 * capabilities; leaf components (FileRow, FileCard, ...) read what they need so
 * flags don't have to be drilled through every layer. The default value keeps
 * standalone component usage working with files-viewer semantics.
 */
export interface FilesConfigValue extends ResolvedFilesCapabilities {
    /** Host supplies FilesApi.cbctProcessing so unprocessed CBCTs open the gate. */
    cbctProcessingEnabled?: boolean;
}

const FilesConfigContext = createContext<FilesConfigValue>(DEFAULT_FILES_CAPABILITIES);

export const FilesConfigProvider = FilesConfigContext.Provider;

export const useFilesConfig = (): FilesConfigValue => useContext(FilesConfigContext);

interface FilesTooltipProps {
    content: React.ReactNode;
    children: React.ReactNode;
}

/**
 * Tooltip used throughout the files module. In 'radix' mode it renders the
 * shared Tooltip — the HOST must mount TooltipProvider at its app root. In
 * 'native' mode it falls back to a plain title attribute (string content only)
 * for hosts that don't mount the provider.
 */
export function FilesTooltip({ content, children }: FilesTooltipProps) {
    const { tooltips } = useFilesConfig();
    if (tooltips === 'native') {
        if (typeof content === 'string' && isValidElement(children)) {
            return cloneElement(children as React.ReactElement, { title: content });
        }
        return <>{children}</>;
    }
    return <Tooltip content={content}>{children}</Tooltip>;
}
